/**
 * wallets.js
 *
 * Client-side helper for saving a whitelist wallet submission to
 * Supabase. Insert-only by design: see supabase/schema.sql for the
 * table + Row Level Security policies that enforce this on the
 * database side too. The format check here is a UX nicety (fast,
 * friendly feedback) — the real security boundary is the database
 * CHECK constraint and RLS policy, which validate independently of
 * whatever this file does.
 */
import { supabase } from './supabaseClient';

const ETH_ADDRESS_RE = /^0x[a-fA-F0-9]{40}$/;

/**
 * Saves a wallet address to the `hexabots_wallets` table.
 *
 * @param {string} walletAddress  Raw address as typed by the player
 * @param {number} [score]       Score at the moment of submission
 * @returns {Promise<{ alreadyExists: boolean }>}
 *   `alreadyExists: true` means this wallet had already claimed a
 *   whitelist spot in an earlier run — not an error from the
 *   player's point of view.
 * @throws {Error} When the address is malformed, Supabase isn't
 *   configured, or the insert fails for any other reason.
 */
export async function saveWallet(walletAddress, score = 0) {
  const normalized = String(walletAddress || '').trim().toLowerCase();

  if (!ETH_ADDRESS_RE.test(normalized)) {
    throw new Error('Invalid ETH wallet — must be 0x + 40 hex chars.');
  }

  const { error } = await supabase
    .from('hexabots_wallets')
    .insert({ wallet_address: normalized, score });

  if (error) {
    // Postgres unique_violation — this wallet already grabbed a spot.
    if (error.code === '23505') {
      return { alreadyExists: true };
    }
    throw error;
  }

  return { alreadyExists: false };
}
